import React, { MutableRefObject, useImperativeHandle } from 'react';
import { useForm } from 'react-hook-form';
import { Info } from '@mui/icons-material';
import { Nullable, SelectOption } from 'global';
import { zodResolver } from '@hookform/resolvers/zod';
import { PaymentType } from '@trnsact/trnsact-shared-types';
import { convertEnumToSelectOptions } from 'utils/selectOptionsHelper';
import { InputField, SelectField, CheckboxField } from 'components/form';
import { Box, Stack, Typography, Tooltip, Divider } from '@mui/material';

import { Template } from '../../../api';
import { stagesMapper } from '../../../mappers';
import { TemplateConstructorMode, TemplateFormValues } from '../../../types';
import { TemplateFormValuesSchema } from '../../../model';
import { initialStagesConstructorFormValue } from '../../../constants';

interface Props {
  isDepartmentsLoading: boolean;
  template: Nullable<Template>;
  mode: TemplateConstructorMode;
  departmentsOptions: SelectOption[];
  submitRef: MutableRefObject<{ submit: () => Promise<TemplateFormValues | null> } | null>;
}

export const StagesConstructorForm = ({
  mode,
  template,
  submitRef,
  departmentsOptions,
  isDepartmentsLoading,
}: Props) => {
  const { control, getValues, trigger } = useForm<TemplateFormValues>({
    mode: 'onChange',
    resolver: zodResolver(TemplateFormValuesSchema),
    defaultValues: template ? stagesMapper.processTemplateToFormValues(template) : initialStagesConstructorFormValue,
  });

  useImperativeHandle(submitRef, () => ({
    submit: async () => {
      const isValid = await trigger();
      return isValid ? getValues() : null;
    },
  }));

  return (
    <Stack gap="1rem">
      <Typography fontWeight={500}>Common Info</Typography>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '1rem' }}>
        <InputField
          control={control}
          name="templateName"
          label="Template Name"
          inputProps={{ required: true, variant: 'outlined' }}
        />

        <SelectField
          required
          control={control}
          variant="outlined"
          label="Department"
          name="businessProcess"
          disabled={mode === 'edit'}
          options={departmentsOptions}
          isLoading={isDepartmentsLoading}
        />

        {mode !== 'edit' && <CheckboxField name="isActive" control={control} label="Make template active" />}
      </Box>

      <Divider />

      <Stack direction="row" alignItems="center" gap="0.5rem">
        <Typography fontWeight={500}>Apply Business Rules</Typography>

        <Tooltip
          arrow
          title={<Typography align="center">Template will attach to new deals based on this rule</Typography>}
        >
          <Info fontSize="small" color="primary" />
        </Tooltip>
      </Stack>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '1rem' }}>
        <SelectField
          required
          control={control}
          variant="outlined"
          label="Payment Type"
          name="applyRules.paymentType"
          options={convertEnumToSelectOptions(PaymentType)}
        />
      </Box>
    </Stack>
  );
};
